import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { router } from '../../router';
import { t } from '../../i18n';
import { icon } from '../ui/icons';

interface Notif { id: string; type: string; title: string; body: string | null; link: string | null; read: number; created_at: string; }

/** <cadence-notification-bell> — bell icon with unread badge and a dropdown panel.
 *  Used in both the admin header and the portal nav. */
@customElement('cadence-notification-bell')
export class CadenceNotificationBell extends LitElement {
  @state() private items: Notif[] = [];
  @state() private open = false;

  static styles = css`
    :host { position: relative; display: inline-block; }
    .bell {
      background: none; border: 1px solid var(--color-border); cursor: pointer;
      color: var(--color-text-secondary); border-radius: var(--radius-md);
      width: 38px; height: 38px; display: inline-grid; place-items: center; position: relative;
      transition: border-color 0.15s, color 0.15s;
    }
    .bell:hover { border-color: var(--color-primary); color: var(--color-primary); }
    svg { width: 20px; height: 20px; }
    .badge {
      position: absolute; top: -6px; right: -6px; min-width: 18px; height: 18px; padding: 0 4px;
      border-radius: 999px; background: var(--color-danger); color: #fff;
      font-size: 0.68rem; font-weight: 700; display: grid; place-items: center;
    }
    .panel {
      position: absolute; top: calc(100% + 8px); right: 0; width: 340px; max-width: 90vw; z-index: 60;
      background: var(--color-bg-primary); border: 1px solid var(--color-border);
      border-radius: var(--radius-md); box-shadow: var(--shadow-lg); overflow: hidden;
    }
    .head { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-bottom: 1px solid var(--color-border); }
    .head strong { font-size: 0.95rem; }
    .head button { background: none; border: none; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.8rem; }
    .list { max-height: 360px; overflow-y: auto; }
    .n { display: block; width: 100%; text-align: left; background: none; border: none; border-bottom: 1px solid var(--color-border); padding: 12px 14px; cursor: pointer; color: var(--color-text-primary); font: inherit; }
    .n:hover { background: var(--color-bg-secondary); }
    .n.unread { background: color-mix(in srgb, var(--color-primary) 7%, transparent); }
    .n .title { font-weight: 600; font-size: 0.88rem; }
    .n.unread .title::before { content: ''; display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: var(--color-primary); margin-right: 6px; vertical-align: middle; }
    .n .body { color: var(--color-text-secondary); font-size: 0.82rem; margin-top: 2px; }
    .n .time { color: var(--color-text-secondary); font-size: 0.72rem; margin-top: 4px; }
    .empty { padding: 20px 14px; text-align: center; color: var(--color-text-secondary); font-size: 0.85rem; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.refresh();
    this.onDocClick = this.onDocClick.bind(this);
    document.addEventListener('click', this.onDocClick);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onDocClick);
  }

  private onDocClick(e: MouseEvent) {
    if (this.open && !e.composedPath().includes(this)) this.open = false;
  }

  private async refresh() {
    try { this.items = await api.get<Notif[]>('/notifications'); } catch { /* ignore */ }
  }

  private get unread() { return this.items.filter((n) => !n.read).length; }

  private async toggle() {
    this.open = !this.open;
    if (this.open) await this.refresh();
  }

  private async pick(n: Notif) {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}`, { read: true }); n.read = 1; this.items = [...this.items]; } catch { /* ignore */ }
    }
    this.open = false;
    if (n.link) router.navigate(n.link);
  }

  private async markAll() {
    try { await api.post('/notifications/mark-all-read'); this.items = this.items.map((n) => ({ ...n, read: 1 })); } catch { /* ignore */ }
  }

  render() {
    const unread = this.unread;
    return html`
      <button class="bell" aria-label=${t('common.notifications.title')} title=${t('common.notifications.title')} @click=${() => this.toggle()}>
        ${icon('bell', 20)}
        ${unread ? html`<span class="badge" aria-label=${t('common.notifications.unread', { count: unread })}>${unread}</span>` : ''}
      </button>
      ${this.open ? html`
        <div class="panel" role="dialog" aria-label=${t('common.notifications.title')}>
          <div class="head">
            <strong>${t('common.notifications.title')}</strong>
            ${this.items.some((n) => !n.read) ? html`<button @click=${() => this.markAll()}>${t('common.notifications.markAllRead')}</button>` : ''}
          </div>
          <div class="list">
            ${this.items.length
              ? this.items.map((n) => html`
                  <button class="n ${n.read ? '' : 'unread'}" @click=${() => this.pick(n)}>
                    <div class="title">${n.title}</div>
                    ${n.body ? html`<div class="body">${n.body}</div>` : ''}
                    <div class="time">${new Date(n.created_at + 'Z').toLocaleString()}</div>
                  </button>`)
              : html`<div class="empty">${t('common.notifications.empty')}</div>`}
          </div>
        </div>` : ''}
    `;
  }
}
